/**
 * Ограничитель залпа однотипных событий перед отправкой в Sentry.
 *
 * Поводом стала REACT-NATIVE-2M — «Server returned handshake error: Handshake
 * was canceled.»: 14 867 событий, из которых 14 748 (99,2 %) принесли ДВА
 * человека, 10 555 и 4 193. В выборке подряд идут события с интервалом меньше
 * секунды — это застрявший цикл переподключения, а не 16 разных случаев.
 *
 * Последствие не в самих событиях, а в том, что они вытеснили всё остальное:
 * за месяц два хаба дали около 97 % всех прод-ошибок, и на их фоне не видно
 * ничего. Поиск за 7 дней и за 30 дней показывал противоположные картины.
 *
 * У ошибок в `Sentry.init` нет ни `sampleRate`, ни дедупликации: бэкенд шлёт
 * 10 % (`client_sample_rate: 0.1` в его событиях), клиент — все 100 %.
 *
 * ## Чего здесь НЕ делается
 *
 * Это не `ignoreErrors`. Заглушить класс ошибок целиком — значит заменить шум
 * тишиной, а тишина неотличима от исправности: ровно тот отказ, выглядящий как
 * успех, из-за которого эту задачу и разбирали. Поэтому:
 *
 * * ПЕРВОЕ событие каждого вида уходит всегда и без изменений;
 * * повторы внутри короткого окна считаются, а не исчезают;
 * * по истечении паузы одно событие уходит снова и несёт на себе, сколько
 *   повторов было проглочено (`burst_dropped`) — так залп остаётся видимым
 *   фактом, а не пропавшим сигналом.
 *
 * То есть мы теряем не знание о проблеме, а её тысячекратный повтор.
 */

/** Сколько событий одного вида пропускаем, прежде чем начать считать. */
export const BURST_ALLOWANCE = 3;

/** Окно, в котором события считаются одним залпом. */
export const BURST_WINDOW_MS = 10_000;

/** Пауза, после которой из того же залпа снова пропускается одно событие. */
export const BURST_RESUME_MS = 60_000;

/**
 * Сколько видов держим в памяти. Ключей у приложения немного, но вкладка живёт
 * часами, а утечка в телеметрии — худший вид утечки: её никто не ищет.
 */
export const BURST_KEYS_MAX = 100;

interface BurstState {
    /** Сколько пропущено в текущем окне. */
    sent: number;
    /** Сколько проглочено с последнего отправленного. */
    dropped: number;
    /** Когда окно началось. */
    windowStartedAt: number;
    /** Когда отправляли в последний раз. */
    lastSentAt: number;
}

const states = new Map<string, BurstState>();

/** Только для тестов: состояние живёт в модуле и переживает экземпляры. */
export function _resetSentryBurstForTest(): void {
    states.clear();
}

/**
 * Подпись события: по чему считаем «то же самое».
 *
 * Берём тип и текст исключения плюс верхний кадр стека. Текста мало: одна и та
 * же фраза может прилетать из разных мест, и схлопнуть их значило бы потерять
 * второе место. Стека тоже мало: у отклонений промисов его часто нет вовсе.
 */
export function burstKey(event: unknown): string {
    const e = event as {
        message?: unknown;
        exception?: { values?: Array<{ type?: unknown; value?: unknown; stacktrace?: { frames?: Array<{ filename?: unknown; function?: unknown; lineno?: unknown }> } }> };
    } | null;
    if (!e) return '';
    const first = e.exception?.values?.[0];
    const type = typeof first?.type === 'string' ? first.type : '';
    const value = typeof first?.value === 'string'
        ? first.value
        : typeof e.message === 'string' ? e.message : '';
    const frames = first?.stacktrace?.frames;
    const top = Array.isArray(frames) && frames.length > 0 ? frames[frames.length - 1] : undefined;
    const where = top
        ? `${String(top.filename ?? '')}:${String(top.function ?? '')}:${String(top.lineno ?? '')}`
        : '';
    return `${type}|${value}|${where}`;
}

/** Выкинуть самый давний вид, когда их стало слишком много. */
function evictOldest(): void {
    if (states.size <= BURST_KEYS_MAX) return;
    let oldestKey: string | null = null;
    let oldestAt = Infinity;
    states.forEach((s, k) => {
        if (s.lastSentAt < oldestAt) { oldestAt = s.lastSentAt; oldestKey = k; }
    });
    if (oldestKey !== null) states.delete(oldestKey);
}

/**
 * Решение по событию: отдать его дальше (возможно, с пометкой о проглоченных)
 * или придержать.
 *
 * Возвращает то же событие, если отправлять, и `null`, если нет. Событие
 * мутируется только в одном случае — когда к нему добавляется счётчик
 * проглоченных; в остальных уходит ровно то, что пришло.
 */
export function throttleSentryEvent<T>(event: T, now: number = Date.now()): T | null {
    if (!event) return event;
    let key: string;
    try {
        key = burstKey(event);
    } catch {
        // Телеметрия не имеет права ронять приложение и не имеет права молча
        // съедать событие: не смогли разобрать — отправляем как есть.
        return event;
    }
    if (!key) return event;

    const state = states.get(key);
    if (!state) {
        states.set(key, { sent: 1, dropped: 0, windowStartedAt: now, lastSentAt: now });
        evictOldest();
        return event;
    }

    // Окно истекло и повторов не копилось — считаем залп законченным.
    if (now - state.windowStartedAt > BURST_WINDOW_MS && state.dropped === 0) {
        state.sent = 1;
        state.windowStartedAt = now;
        state.lastSentAt = now;
        return event;
    }

    if (state.sent < BURST_ALLOWANCE) {
        state.sent += 1;
        state.lastSentAt = now;
        return event;
    }

    // Залп идёт. Пропускаем одно событие раз в паузу — и только вместе со
    // счётчиком, иначе по нему не отличить «случилось ещё раз» от «идёт тысяча
    // раз подряд».
    if (now - state.lastSentAt >= BURST_RESUME_MS) {
        const dropped = state.dropped;
        state.dropped = 0;
        state.sent = 1;
        state.windowStartedAt = now;
        state.lastSentAt = now;
        return withDroppedCount(event, dropped);
    }

    state.dropped += 1;
    return null;
}

/** Пометить событие числом проглоченных повторов. */
function withDroppedCount<T>(event: T, dropped: number): T {
    if (dropped <= 0) return event;
    try {
        const e = event as unknown as { tags?: Record<string, unknown>; extra?: Record<string, unknown> };
        e.tags = { ...(e.tags ?? {}), burst_dropped: String(dropped) };
        e.extra = { ...(e.extra ?? {}), burst_dropped: dropped };
    } catch {
        // Не смогли пометить — всё равно отправляем: событие важнее пометки.
    }
    return event;
}
